import * as XLSX from 'xlsx';

// Nombre válido para hoja Excel: sin /\\?*[]:, max 31 chars.
export const sanitizeSheetName = (name, fallback = 'Curso') => {
  const cleaned = String(name || fallback).replace(/[\\/?*[\]:]/g, '-').trim();
  return cleaned.substring(0, 31) || fallback;
};

// Nombre válido para archivo: ASCII seguro, sin espacios.
export const sanitizeFileName = (name) =>
  String(name || '')
    .normalize('NFKD')
    .replace(/[^\w\d-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const isMc = (e) => (e.resource?.type || e.type) === 'masterclass';
const getCourseId = (e) => e.resource?.courseId || e.courseId || '_none';
const getGroupNumber = (e) => e.groupNumber || e.resource?.groupNumber || 1;

// Construye el workbook con una hoja por curso.
export const buildAgendaWorkbook = ({ instructor, courses, events }) => {
  const wb = XLSX.utils.book_new();
  const instructorName = instructor?.name || 'Instructor';

  // Agrupar por curso
  const byCourse = new Map();
  events.forEach((e) => {
    const cId = getCourseId(e);
    if (!byCourse.has(cId)) byCourse.set(cId, []);
    byCourse.get(cId).push(e);
  });

  for (const [cId, evs] of byCourse.entries()) {
    const course = courses.find((c) => c.id === cId);
    const courseName = course?.name || course?.title || 'Sin curso';
    const mcPerGrp = course?.totalMasterclasses || course?.masterclassesPerGroup || 0;
    const clPerGrp = course?.totalSessions || course?.classesPerGroup || 0;

    // Sub-agrupar por groupNumber
    const byGrp = new Map();
    evs.forEach((e) => {
      const gn = getGroupNumber(e);
      if (!byGrp.has(gn)) byGrp.set(gn, []);
      byGrp.get(gn).push(e);
    });

    const rows = [
      ['Instructor:', instructorName],
      ['Curso:', courseName],
      [],
      ['Grupo', 'Fecha', 'Día', 'Inicio', 'Fin', 'Tipo', 'N°'],
    ];

    const groupNums = [...byGrp.keys()].sort((a, b) => a - b);
    for (const gn of groupNums) {
      const grpEvents = byGrp
        .get(gn)
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start));
      let mcCount = 0;
      let clCount = 0;
      for (const ev of grpEvents) {
        const cancelled = ev.status === 'cancelled';
        const mc = isMc(ev);
        let numero = '';
        if (cancelled) {
          numero = '✕';
        } else if (mc) {
          mcCount++;
          numero = mcPerGrp > 0 ? ((mcCount - 1) % mcPerGrp) + 1 : mcCount;
        } else {
          clCount++;
          numero = clPerGrp > 0 ? ((clCount - 1) % clPerGrp) + 1 : clCount;
        }
        const startD = new Date(ev.start);
        const endD = new Date(ev.end);
        rows.push([
          `G${gn}`,
          startD.toLocaleDateString('es-PE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          startD.toLocaleDateString('es-PE', { weekday: 'long' }),
          startD.toLocaleTimeString('es-PE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
          endD.toLocaleTimeString('es-PE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
          mc ? 'MC' : 'CL',
          numero,
        ]);
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 8 },
      { wch: 8 },
      { wch: 6 },
      { wch: 6 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(courseName));
  }

  return wb;
};

const exportAgendaToXlsx = ({ instructor, courses, events, toast, isGroupInactive, fileName: customFileName }) => {
  const eventsToExport = isGroupInactive
    ? events.filter(e => !isGroupInactive(
        e.resource?.courseId || e.courseId || '_none',
        e.groupNumber || e.resource?.groupNumber || 1
      ))
    : events;
  if (!eventsToExport || eventsToExport.length === 0) {
    toast?.error?.('Sin eventos para exportar');
    return;
  }
  try {
    const wb = buildAgendaWorkbook({ instructor, courses, events: eventsToExport });
    const today = new Date().toISOString().slice(0, 10);
    const fileName = customFileName || `Agenda_${sanitizeFileName(
      instructor?.name || 'Instructor'
    )}_${today}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast?.success?.('Excel descargado');
  } catch (err) {
    console.error('exportAgendaToXlsx error:', err);
    toast?.error?.('Error al generar el Excel');
  }
};

export default exportAgendaToXlsx;
